import { useLocation } from "@remix-run/react";
import { useEffect } from "react";
import { PAGE_SECTION_IDS, resolvePageSectionId } from "~/lib/constants/pageSectionIds";

function getPageSectionElement(sectionId: string): HTMLElement | null {
  if (typeof document === "undefined") return null;
  const resolved = resolvePageSectionId(sectionId) || sectionId;
  const candidates =
    resolved === PAGE_SECTION_IDS.listings
      ? [PAGE_SECTION_IDS.listings, PAGE_SECTION_IDS.workspaceListings]
      : resolved === PAGE_SECTION_IDS.workspaceListings
        ? [PAGE_SECTION_IDS.workspaceListings, PAGE_SECTION_IDS.listings]
        : [resolved];

  for (const id of candidates) {
    const el = document.getElementById(id);
    if (el) return el;
  }
  return null;
}

/** Smooth-scroll to a section by element id. Returns true if the element existed. */
export function scrollToPageSection(
  sectionId: string,
  behavior: ScrollBehavior = "smooth",
): boolean {
  const el = getPageSectionElement(sectionId);
  if (!el) return false;
  el.scrollIntoView({ behavior, block: "start" });
  return true;
}

/**
 * Smooth-scrolls to `#section-id` on any page when that element exists.
 * Retries briefly so deferred/Suspense sections can mount first.
 * Also intercepts in-page hash link clicks for animated scrolling.
 */
export function usePageSectionScroll() {
  const location = useLocation();

  useEffect(() => {
    const hash = location.hash?.replace(/^#/, "").trim();
    if (!hash) return;
    const sectionId = resolvePageSectionId(hash) || hash;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 40;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const tryScroll = () => {
      if (cancelled) return;
      if (scrollToPageSection(sectionId, "smooth")) return;

      attempts += 1;
      if (attempts < maxAttempts) {
        retryTimer = setTimeout(tryScroll, 50);
      }
    };

    const startTimer = setTimeout(tryScroll, 80);

    return () => {
      cancelled = true;
      clearTimeout(startTimer);
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [location.hash, location.pathname]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const anchor = target?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (!href || !href.includes("#")) return;

      let url: URL;
      try {
        url = new URL(href, window.location.href);
      } catch {
        return;
      }

      if (url.origin !== window.location.origin) return;
      if (url.pathname !== window.location.pathname) return;

      const rawId = url.hash.replace(/^#/, "").trim();
      const sectionId = resolvePageSectionId(rawId) || rawId;
      if (!sectionId || !getPageSectionElement(sectionId)) return;

      event.preventDefault();
      if (window.location.hash !== url.hash) {
        window.history.pushState(null, "", url.hash);
      }
      scrollToPageSection(sectionId, "smooth");
    };

    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);
}
